import { useCallback, useEffect, useRef } from 'react'
import { Stack, styled } from '@mui/material'
import { getFileFromUser } from '@weblite/file'
import { RawImageMessage } from '@weblite/ts-action/message'
import { AppBarBackTitleReplace } from '$common/app-bar/app-bar-back-title-replace'
import { ActionHandler } from '$common/action-handler'
import { useContentFileStatus } from '$common/react-hooks/message'
import { MediaCard } from '$common/media-card'
import { useFileObjectUrl } from '$func/file'
import { t } from '$i18n'
import { validateMessageFileSizeAndShowError } from '$logic/message/helper'

const StyledImage = styled(`img`)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

interface Props {
  attachment: RawImageMessage['attachment']
  onCancel: () => unknown
  setSelectedFile: (file: File) => unknown
  canReplace?: boolean
  newFile?: File
}

export function EditImage({
  attachment,
  onCancel,
  setSelectedFile,
  canReplace,
  newFile,
}: Props) {
  const { fileId, base64: thumbnail } = attachment

  const {
    state,
    percentage,
    blobURL: imageBlobUrl,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId: '', messageType: 'IMAGE' })

  const cardRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const newFileObjectUrl = useFileObjectUrl(newFile)

  const source = newFileObjectUrl || imageBlobUrl || thumbnail

  useEffect(() => {
    if (newFileObjectUrl || imageBlobUrl) return
    startDownload()
  }, [newFileObjectUrl, imageBlobUrl, startDownload])

  return (
    <MediaCard
      sxProps={{ position: 'fixed', top: 0 }}
      cardRef={cardRef}
      appbarComponent={
        <AppBarBackTitleReplace
          title={t('chat.editImage')}
          canReplace={canReplace}
          onBack={onCancel}
          onReplace={() =>
            getFileFromUser('image/*', (file: File) => {
              if (validateMessageFileSizeAndShowError(file))
                setSelectedFile(file)
            })
          }
        />
      }
    >
      <Stack
        sx={{
          position: 'absolute',
          marginTop: 0,
          width: '100%',
          height: '100%',
        }}
      >
        {state !== 'AVAILABLE' && (
          <ActionHandler
            size={50}
            state={state}
            percentage={percentage}
            onCancelClick={cancelOperation}
            onDownloadClick={startDownload}
            sx={{
              backgroundColor: '#505962B2',
            }}
            rootSx={{
              position: 'absolute',
              left: 'calc(50% - 25px)',
              top: 'calc(50% - 25px)',
              zIndex: 1,
            }}
          />
        )}
        <StyledImage ref={imageRef} src={source} alt="alt text" />
      </Stack>
    </MediaCard>
  )
}
